import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import { 
  Building2, 
  Home, 
  Hotel, 
  ShoppingBag, 
  GraduationCap,
  Award,
  FileCheck,
  Shield,
  ArrowRight,
  Quote,
  Droplets,
  Leaf,
  Wrench,
  Monitor
} from 'lucide-react';

// Hero Section
const HeroSection = () => (
  <section className="relative min-h-[600px] lg:min-h-[700px] flex items-center" data-testid="hero-section">
    <div 
      className="absolute inset-0 bg-cover bg-center"
      style={{
        backgroundImage: `url('https://customer-assets.emergentagent.com/job_cd4f5c4d-92f8-4312-8a77-df80f7c24853/artifacts/b5uxvm8n_iStock-2155879474.jpg')`
      }}
    />
    <div className="absolute inset-0 bg-gradient-to-r from-[#1a2e44]/90 via-[#1a2e44]/70 to-[#1a2e44]/40" />
    
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
      <div className="max-w-2xl">
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold text-white leading-tight mb-6">
          Refined Exterior Care.<br />Thoughtfully Managed.
        </h1>
        <p className="text-lg md:text-xl text-slate-200 mb-10 leading-relaxed">
          Consistent oversight and detailed attention designed to preserve the beauty and long-term value of exceptional properties.
        </p>
        <div className="flex flex-col sm:flex-row gap-4">
          <Link
            to="/contact?intent=consultation"
            className="inline-block bg-[#1a2e44] hover:bg-[#0f1d2d] text-white font-medium py-4 px-8 text-center border border-white/20 transition-colors"
            data-testid="hero-cta"
          >
            Schedule a Private Consultation
          </Link>
          <Link
            to="/services"
            className="inline-block bg-transparent hover:bg-white/10 text-white font-medium py-4 px-8 text-center border border-white/40 transition-colors"
            data-testid="hero-secondary-cta"
          >
            View Our Services
          </Link>
        </div>
      </div>
    </div>
  </section>
);

// Credibility Section
const CredibilitySection = () => {
  const items = [
    { title: 'Structured Service Protocols', description: 'Documented procedures' },
    { title: 'Dedicated Account Oversight', description: 'Direct communication' },
    { title: 'Documented Service Delivery', description: 'Detailed reporting' }
  ];

  return (
    <section className="bg-white py-12 border-b border-slate-200" data-testid="credibility-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {items.map((item, index) => (
            <div 
              key={index}
              className="text-center p-6 border-t-4 border-[#1a2e44] bg-slate-50"
            >
              <span className="block text-lg font-bold text-[#1a2e44] mb-1">
                {item.title}
              </span>
              <span className="text-sm text-slate-600">
                {item.description}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  const services = [
    {
      icon: Leaf,
      title: 'Landscape Maintenance',
      image: 'https://customer-assets.emergentagent.com/job_cd4f5c4d-92f8-4312-8a77-df80f7c24853/artifacts/r7pg4pr4_iStock-2155879454.jpg',
      link: '/landscape-maintenance'
    },
    {
      icon: Droplets,
      title: 'Irrigation Management',
      image: 'https://customer-assets.emergentagent.com/job_cd4f5c4d-92f8-4312-8a77-df80f7c24853/artifacts/y7iby1hw_iStock-1428027852.jpg',
      link: '/irrigation-management'
    },
    {
      icon: Droplets,
      title: 'Pressure Washing',
      image: 'https://customer-assets.emergentagent.com/job_cd4f5c4d-92f8-4312-8a77-df80f7c24853/artifacts/mrf246m2_iStock-2155879493.jpg',
      link: '/pressure-washing'
    },
    {
      icon: Wrench,
      title: 'Exterior Asset Care',
      image: 'https://customer-assets.emergentagent.com/job_cd4f5c4d-92f8-4312-8a77-df80f7c24853/artifacts/e0gkd1b5_iStock-2175972769.jpg',
      link: '/exterior-asset-care'
    }
  ];

  return (
    <section className="py-20 bg-white" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-[#1a2e44] mb-4">
            Our Services
          </h2>
          <div className="w-20 h-1 bg-[#1a2e44] mx-auto" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((service, index) => (
            <Link
              key={index}
              to={service.link}
              className="group block bg-white border border-slate-200 overflow-hidden hover:shadow-xl transition-all duration-300"
            >
              <div className="relative h-56 overflow-hidden">
                <img 
                  src={service.image} 
                  alt={service.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  loading="eager"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1a2e44]/60 to-transparent" />
              </div>
              <div className="p-4 text-center border-t-4 border-[#1a2e44]">
                <h3 className="text-base font-bold text-[#1a2e44]">
                  {service.title}
                </h3>
              </div>
            </Link>
          ))}
        </div>

        <div className="text-center mt-10">
          <Link
            to="/services"
            className="inline-flex items-center gap-2 bg-[#1a2e44] hover:bg-[#0f1d2d] text-white font-bold py-3 px-8 uppercase tracking-wide text-sm transition-all"
            data-testid="view-all-services"
          >
            View All Services <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};

// Industries Section
const IndustriesSection = () => {
  const industries = [
    { icon: Home, name: 'Private Residences' },
    { icon: Building2, name: 'HOAs & Communities' },
    { icon: Building2, name: 'Corporate Campuses' },
    { icon: ShoppingBag, name: 'Retail Centers' }
  ];

  return (
    <section className="py-16 bg-slate-50" data-testid="industries-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-4xl font-bold text-[#1a2e44] mb-4">
            Industries We Serve
          </h2>
          <div className="w-20 h-1 bg-[#1a2e44] mx-auto" />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {industries.map((industry, index) => (
            <div
              key={index}
              className="flex flex-col items-center p-6 bg-white border border-slate-200 hover:border-[#1a2e44] transition-colors"
            >
              <industry.icon className="w-12 h-12 text-[#1a2e44] mb-3" strokeWidth={1.5} />
              <span className="text-sm font-semibold text-[#1a2e44] text-center">
                {industry.name}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Why Choose Us Section
const WhyChooseSection = () => {
  const benefits = [
    {
      icon: Award,
      title: 'Professionally Trained Groundskeepers',
      description: 'Uniformed and vetted. Following documented service systems and commercial property standards.'
    },
    {
      icon: FileCheck,
      title: 'Advanced Documentation',
      description: 'Photo service documentation, before-and-after images, and detailed inspection reports.'
    },
    {
      icon: Shield,
      title: 'Licensed & Insured',
      description: 'Commercial-grade compliance.'
    },
    {
      icon: Monitor,
      title: 'Enterprise Level Technology',
      description: 'Know what\'s going on from your dashboard with real-time updates and service reports.'
    }
  ];

  return (
    <section className="relative py-20 bg-[#1a2e44]" data-testid="why-choose-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-10">
              Why Choose Sunlight?
            </h2>

            <div className="space-y-6">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex gap-5 items-start min-h-[80px]">
                  <div className="flex-shrink-0 w-14 h-14 bg-white/20 flex items-center justify-center rounded">
                    <benefit.icon className="w-7 h-7 text-white" />
                  </div>
                  <div className="pt-1">
                    <h3 className="text-lg font-bold text-white mb-1">
                      {benefit.title}
                    </h3>
                    <p className="text-slate-300 text-sm leading-relaxed">
                      {benefit.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right - CTA Card */}
          <div className="relative">
            <div 
              className="h-[500px] bg-cover bg-center rounded-lg overflow-hidden"
              style={{
                backgroundImage: `url('https://customer-assets.emergentagent.com/job_52a5d06f-ead8-45ec-936b-a20fd0ee734b/artifacts/6ibf9j5a_iStock-2155879454.jpg')`
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-t from-[#1a2e44] via-transparent to-transparent" />
            </div>
            <div className="absolute bottom-0 left-0 right-0 p-8 text-center">
              <h3 className="text-2xl font-bold text-white mb-4">
                Schedule Your Site Evaluation
              </h3>
              <Link
                to="/contact?intent=assessment"
                className="inline-block bg-[#1a2e44] hover:bg-[#0f1d2d] text-white font-bold py-3 px-8 uppercase tracking-wide text-sm transition-all border border-white/30"
                data-testid="schedule-evaluation"
              >
                Request Consultation
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Partners Section
const PartnersSection = () => (
  <section className="py-12 bg-[#1a2e44] border-t border-slate-700" data-testid="partners-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex flex-wrap items-center justify-center gap-12">
        {['ISSA', 'BOMA', 'USGBC', 'PLANET'].map((partner, index) => (
          <span 
            key={index}
            className="text-2xl font-bold text-slate-500 uppercase tracking-widest"
          >
            {partner}
          </span>
        ))}
      </div>
      <p className="text-center text-slate-400 text-sm mt-6">
        Committed to Excellence in Grounds Care
      </p>
    </div>
  </section>
);

// CTA Break Section
const CTABreakSection = () => (
  <section className="bg-slate-50 py-6 border-t border-slate-200" data-testid="cta-break-section">
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8">
      <p className="text-slate-700">
        Ready to discuss your property's exterior maintenance needs?
      </p>
      <Link
        to="/contact?intent=consultation"
        className="inline-block bg-[#1a2e44] hover:bg-[#0f1d2d] text-white font-bold py-2 px-6 uppercase tracking-wide text-sm transition-all"
        data-testid="cta-break-btn"
      >
        Get in Touch
      </Link>
    </div>
  </section>
);

// Main HomePage Component
export default function HomePage() {
  useEffect(() => {
    document.title = 'Sunlight Grounds Care | Refined Exterior Care';
  }, []);

  return (
    <div data-testid="home-page">
      <HeroSection />
      <CredibilitySection />
      <ServicesSection />
      <IndustriesSection />
      <WhyChooseSection />
      <CTABreakSection />
    </div>
  );
}
